import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
  { label: "专题", to: "/special/s1" },
  { label: "关于", to: "/about" },
  { label: "联系", to: "/contact" },
];

interface SiteNavbarProps {
  overlay?: boolean;
}

export default function SiteNavbar({ overlay = false }: SiteNavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = !overlay || scrolled;

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const term = keyword.trim();
    if (!term) return;
    navigate(`/search?q=${encodeURIComponent(term)}`);
    setKeyword("");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-background-100/95 backdrop-blur border-b border-background-200/70"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="h-16 md:h-20 flex items-center gap-3 md:gap-6">
          <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="91香蕉视频 首页">
            <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-heading font-extrabold text-sm">
              91
            </span>
            <span className="font-heading font-extrabold text-lg text-foreground-950 tracking-tight">
              香蕉视频
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 flex-1">
            {navItems.map((item) => {
              const active =
                item.to === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    active
                      ? "text-primary-400"
                      : "text-foreground-700 hover:text-foreground-950 hover:bg-background-200/60"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <form
            onSubmit={handleSearch}
            className="hidden md:flex items-center ml-auto bg-background-200/60 border border-background-300/60 rounded-full pl-3 pr-1 py-1 w-44 lg:w-56 focus-within:border-primary-400 transition-colors"
          >
            <i className="ri-search-line text-foreground-500 text-base w-4 h-4 flex items-center justify-center" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视内容"
              className="flex-1 bg-transparent px-2 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none"
            />
            <button
              type="submit"
              className="w-7 h-7 rounded-full bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer shrink-0"
              aria-label="搜索"
            >
              <i className="ri-arrow-right-line text-sm" />
            </button>
          </form>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="lg:hidden ml-auto w-10 h-10 rounded-md flex items-center justify-center text-foreground-900 hover:bg-background-200/60 transition-colors cursor-pointer"
            aria-label="打开菜单"
          >
            <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-background-100 border-t border-background-200/70 animate-fade-in">
          <div className="px-4 py-4 space-y-4">
            <form
              onSubmit={handleSearch}
              className="flex items-center bg-background-200/60 border border-background-300/60 rounded-full pl-3 pr-1 py-1"
            >
              <i className="ri-search-line text-foreground-500 text-base w-4 h-4 flex items-center justify-center" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视内容"
                className="flex-1 bg-transparent px-2 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none"
              />
              <button
                type="submit"
                className="w-7 h-7 rounded-full bg-primary-500 text-background-50 flex items-center justify-center shrink-0"
                aria-label="搜索"
              >
                <i className="ri-arrow-right-line text-sm" />
              </button>
            </form>
            <nav className="grid grid-cols-2 gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="px-3 py-3 rounded-lg bg-background-200/50 text-sm font-medium text-foreground-800 hover:text-primary-400 transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}